import { ToastContext } from "context/ToastContext";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { Dropdown } from "primereact/dropdown";
import { InputText } from "primereact/inputtext";
import { Message } from "primereact/message";
import { useContext, useMemo, useState } from "react";
import { useGetAllAccountsQuery } from "store/queries/account";
import {
  useCreateSftpOutboundReportMutation,
  useUpdateSftpOutboundReportMutation,
} from "store/queries/sftpPartners";
import {
  ISftpOutboundReport,
  ISftpPartnerDetail,
  SftpOutboundReportType,
} from "types/SftpPartner";

import {
  outboundReportTypeLabel,
  SFTP_FILE_PREFIX_RE,
  SFTP_FOLDER_RE,
  sftpApiErrorMessage,
  sftpReservedFolderError,
} from "../../utils/sftpPartners";

const DEFAULT_REPORT_TYPE: SftpOutboundReportType = "PLASTICS_PAYMENTS";
const DEFAULT_FOLDER = "reports/";
const DEFAULT_FILE_NAME_PREFIX = "CLEARHEALTH_PLASTICS_PAYMENTS";

const REPORT_TYPE_OPTIONS: {
  label: string;
  value: SftpOutboundReportType;
}[] = [
  {
    label: outboundReportTypeLabel("PLASTICS_PAYMENTS"),
    value: "PLASTICS_PAYMENTS",
  },
];

function HelpText({ children }: { children: any }) {
  return <small className="block text-600 mt-1">{children}</small>;
}

interface IOutboundReportDialogProps {
  partner: ISftpPartnerDetail;
  // Absent = create.
  report?: ISftpOutboundReport;
  onHide: () => void;
}

// Create or edit an outgoing report on an SFTP partner (CLE-2864). Mount it when it opens (the
// parent renders it conditionally), so every open starts from the report or the defaults.
export function OutboundReportDialog({
  partner,
  report,
  onHide,
}: IOutboundReportDialogProps) {
  const isEdit = !!report;
  const toastRef = useContext(ToastContext);
  const [createReport, { isLoading: creating }] =
    useCreateSftpOutboundReportMutation();
  const [updateReport, { isLoading: updating }] =
    useUpdateSftpOutboundReportMutation();
  const saving = creating || updating;

  const [reportType, setReportType] = useState<SftpOutboundReportType>(
    report?.reportType ?? DEFAULT_REPORT_TYPE
  );
  const [accountId, setAccountId] = useState(
    report?.accountId ?? partner.accountId
  );
  const [folder, setFolder] = useState(report?.folder ?? DEFAULT_FOLDER);
  const [fileNamePrefix, setFileNamePrefix] = useState(
    report?.fileNamePrefix ?? DEFAULT_FILE_NAME_PREFIX
  );
  const [serverError, setServerError] = useState<string | null>(null);

  const { data: accountsData, isFetching: accountsLoading } =
    useGetAllAccountsQuery();

  const accountOptions = useMemo(() => {
    const opts = (accountsData?.data ?? [])
      .map((a) => ({ label: a.name, value: a.id }))
      .sort((a, b) => a.label.localeCompare(b.label));
    // The current account may be archived or gone; keep it selectable so the dialog shows it.
    const current = report
      ? { id: report.accountId, name: report.accountName }
      : { id: partner.accountId, name: partner.accountName };
    if (current.id && !opts.some((o) => o.value === current.id)) {
      opts.unshift({
        label: current.name ?? `${current.id} (not found)`,
        value: current.id,
      });
    }
    return opts;
  }, [accountsData, report, partner]);

  // Client-side checks — the server repeats all of them and is the authority.
  const errors: string[] = [];
  if (!accountId) errors.push("Choose an account.");
  if (!SFTP_FOLDER_RE.test(folder))
    errors.push(
      'Folder must end with "/" (e.g. reports/), with no leading "/" and no spaces.'
    );
  else {
    const reserved = sftpReservedFolderError(folder);
    if (reserved) errors.push(reserved);
  }
  if (!SFTP_FILE_PREFIX_RE.test(fileNamePrefix))
    errors.push(
      "File name prefix: letters, digits, _ and - only, starting with a letter or digit."
    );
  const canSubmit = !saving && errors.length === 0;

  const onSubmit = async () => {
    if (!canSubmit) return;
    setServerError(null);
    const body = { accountId, folder, fileNamePrefix };
    try {
      if (report) {
        await updateReport({
          slug: partner.slug,
          id: report.id,
          body,
        }).unwrap();
      } else {
        await createReport({
          slug: partner.slug,
          body: { reportType, ...body },
        }).unwrap();
      }
      toastRef?.current?.show({
        severity: "success",
        summary: isEdit ? "Outgoing report saved" : "Outgoing report created",
        detail: isEdit
          ? `The ${outboundReportTypeLabel(reportType)} was saved.`
          : `The ${outboundReportTypeLabel(
              reportType
            )} was created disabled. Use Send now for a first file, then Enable to start the weekly file.`,
        life: 6000,
      });
      onHide();
    } catch (error) {
      setServerError(
        sftpApiErrorMessage(error, "Couldn't save the outgoing report.")
      );
    }
  };

  const footer = (
    <div className="flex gap-2 justify-content-end">
      <Button
        type="button"
        label="Cancel"
        className="p-button-text"
        onClick={onHide}
        disabled={saving}
      />
      <Button
        type="button"
        label={isEdit ? "Save changes" : "Add report"}
        icon="pi pi-check"
        loading={saving}
        disabled={!canSubmit}
        onClick={onSubmit}
        data-testid="sftp-outbound-dialog-submit"
      />
    </div>
  );

  return (
    <Dialog
      visible
      header={isEdit ? "Edit outgoing report" : "Add outgoing report"}
      footer={footer}
      onHide={onHide}
      style={{ width: "640px", maxWidth: "95vw" }}
    >
      <div
        className="flex flex-column gap-3"
        data-testid="sftp-outbound-dialog"
      >
        {serverError ? (
          <Message
            severity="error"
            text={serverError}
            data-testid="sftp-outbound-dialog-error"
          />
        ) : null}

        <div>
          <label
            htmlFor="sftp-outbound-type"
            className="block font-semibold mb-2"
          >
            Report
          </label>
          <Dropdown
            inputId="sftp-outbound-type"
            value={reportType}
            options={REPORT_TYPE_OPTIONS}
            onChange={(e) => setReportType(e.value)}
            className="w-full"
            disabled={isEdit || saving}
            appendTo={document.body}
          />
          {isEdit ? (
            <HelpText>The report can&apos;t be changed.</HelpText>
          ) : null}
        </div>

        <div>
          <label
            htmlFor="sftp-outbound-account"
            className="block font-semibold mb-2"
          >
            Account
          </label>
          <Dropdown
            inputId="sftp-outbound-account"
            value={accountId}
            options={accountOptions}
            onChange={(e) => setAccountId(e.value ?? "")}
            placeholder={accountsLoading ? "Loading…" : "Select an account"}
            filter
            filterBy="label"
            className="w-full"
            disabled={saving}
            appendTo={document.body}
          />
          <HelpText>The account whose data the file contains.</HelpText>
        </div>

        <div>
          <label
            htmlFor="sftp-outbound-folder"
            className="block font-semibold mb-2"
          >
            Folder
          </label>
          <InputText
            id="sftp-outbound-folder"
            value={folder}
            onChange={(e) => setFolder(e.target.value)}
            disabled={saving}
            className="w-full"
          />
          <HelpText>
            Inside the partner&apos;s folder. Must not be a folder the import
            reads (835/, 837/, orders/).
          </HelpText>
        </div>

        <div>
          <label
            htmlFor="sftp-outbound-prefix"
            className="block font-semibold mb-2"
          >
            File name prefix
          </label>
          <InputText
            id="sftp-outbound-prefix"
            value={fileNamePrefix}
            onChange={(e) => setFileNamePrefix(e.target.value)}
            disabled={saving}
            className="w-full"
          />
          <HelpText>
            Files are written as{" "}
            <code data-testid="sftp-outbound-dialog-example">
              {`${partner.s3Prefix}${folder}${fileNamePrefix}_YYYYMMDD.csv`}
            </code>{" "}
            (a range of days: <code>_YYYYMMDD-YYYYMMDD.csv</code>).
          </HelpText>
        </div>

        {!isEdit ? (
          <HelpText>
            New reports are created <strong>disabled</strong>. Use Send now for
            a first file, then Enable to start the weekly file.
          </HelpText>
        ) : null}

        {errors.length ? (
          <ul
            className="p-error m-0 pl-4"
            data-testid="sftp-outbound-dialog-errors"
          >
            {errors.map((e) => (
              <li key={e}>{e}</li>
            ))}
          </ul>
        ) : null}
      </div>
    </Dialog>
  );
}
